import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Area, AreaChart, Cell,
  ReferenceDot,
} from 'recharts';
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, AlertTriangle,
  ChevronDown, ChevronRight, ChevronLeft, DollarSign, Target, BarChart3, Users,
} from 'lucide-react';

const formatINR = (v) => {
  if (v == null || isNaN(v)) return '--';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(v);
};

const formatShortINR = (v) => {
  if (v == null || isNaN(v)) return '--';
  if (v >= 100000) return `\u20B9${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return formatINR(v);
};

const displayClosures = (val) => {
  if (val == null) return '--';
  if (val > 1000) return '--'; // Sanity: closure count shouldn't exceed 1000/day
  return val.toLocaleString('en-IN');
};

const trendIcon = (trend) => {
  if (trend === 'improving' || trend === 'up')
    return <TrendingUp className="w-4 h-4 text-emerald-400" />;
  if (trend === 'declining' || trend === 'down')
    return <TrendingDown className="w-4 h-4 text-red-400" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

const trendColor = (trend) => {
  if (trend === 'improving' || trend === 'up') return 'text-emerald-400';
  if (trend === 'declining' || trend === 'down') return 'text-red-400';
  return 'text-slate-400';
};

const severityStyles = {
  critical: 'bg-red-500/10 border-red-500/40 text-red-400',
  warning: 'bg-amber-500/10 border-amber-500/40 text-amber-400',
  info: 'bg-blue-500/10 border-blue-500/40 text-blue-400',
  success: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400',
};

export default function TLPerformanceDetail() {
  const { tlId } = useParams();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() }; // 0-indexed
  });
  const [selectedWeek, setSelectedWeek] = useState(null); // null until auto-set, 1-5 = specific week
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [expandedGym, setExpandedGym] = useState(null);
  const [expandedFlag, setExpandedFlag] = useState(null);

  // Compute weeks for the selected month
  const weeks = useMemo(() => {
    const { year, month } = selectedMonth;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result = [];
    let weekStart = 1;
    let weekNum = 1;
    while (weekStart <= daysInMonth) {
      const weekEnd = Math.min(weekStart + 6, daysInMonth);
      result.push({
        num: weekNum,
        label: `W${weekNum}`,
        sub: `${weekStart}-${weekEnd}`,
        startDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(weekStart).padStart(2, '0')}`,
        endDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(weekEnd).padStart(2, '0')}`,
        days: weekEnd - weekStart + 1,
      });
      weekStart = weekEnd + 1;
      weekNum++;
    }
    return result;
  }, [selectedMonth]);

  // Auto-select current week on load or month change
  useEffect(() => {
    const now = new Date();
    if (selectedMonth.year === now.getFullYear() && selectedMonth.month === now.getMonth()) {
      const day = now.getDate();
      const weekIdx = weeks.findIndex(w => {
        const [start, end] = w.sub.split('-').map(Number);
        return day >= start && day <= end;
      });
      setSelectedWeek(weekIdx >= 0 ? weeks[weekIdx].num : weeks[weeks.length - 1].num);
    } else {
      setSelectedWeek(1); // default to W1 for other months
    }
  }, [selectedMonth, weeks]);

  // Fetch data when week selection changes
  useEffect(() => {
    const activeWeek = weeks.find(w => w.num === selectedWeek);
    if (!activeWeek) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    axiosInstance
      .get(`/tl-performance/${encodeURIComponent(tlId)}`, {
        params: { days: activeWeek.days, end_date: activeWeek.endDate }
      })
      .then((res) => { if (!cancelled) { setData(res.data); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.response?.data?.detail || 'Failed to load'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [tlId, selectedWeek, weeks]);

  const chartData = useMemo(() => {
    if (!data?.daily) return [];
    return [...data.daily].reverse().map((d) => ({
      date: d.date?.slice(5) || '',
      fullDate: d.date,
      revenue: d.revenue_done || 0,
      target: d.target || 0,
      pct: d.completion_pct || 0,
      isBest: d.date === data.summary?.best_day?.date,
      isWorst: d.date === data.summary?.worst_day?.date,
    }));
  }, [data]);

  if (loading) return (<div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="flex flex-col items-center gap-3"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /><span className="text-slate-400 text-sm">Loading performance data...</span></div></div>);
  if (error) return (<div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="text-center"><AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" /><p className="text-red-400 text-sm mb-4">{error}</p><button onClick={() => navigate('/leaderboard')} className="text-sm text-emerald-400 hover:text-emerald-300 underline">Back to Leaderboard</button></div></div>);
  if (!data) return null;

  const { summary, daily, gyms, flags, period } = data;
  const totalClosures = (daily || []).reduce((sum, d) => sum + (d.closures || 0), 0);
  const totalTarget = summary?.total_target || (daily || []).reduce((s, d) => s + (d.target || 0), 0);
  const avgDailyTarget = (daily && daily.length > 0) ? totalTarget / daily.length : 0;
  const activeWeek = weeks.find(w => w.num === selectedWeek);
  const weekLabel = activeWeek ? `W${activeWeek.num}` : '';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/leaderboard')} className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors"><ArrowLeft className="w-5 h-5 text-slate-400" /></button>
            <div>
              <h1 className="text-lg font-bold text-white">{data.tl_name}</h1>
              <p className="text-xs text-slate-500">
                {(() => {
                  const activeWeek = weeks.find(w => w.num === selectedWeek);
                  if (!activeWeek) return '';
                  const fmt = (ds) => {
                    const d = new Date(ds + 'T00:00:00');
                    return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
                  };
                  const yr = selectedMonth.year;
                  return `${fmt(activeWeek.startDate)} - ${fmt(activeWeek.endDate)}, ${yr}`;
                })()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Month navigation */}
            <button onClick={() => setSelectedMonth(prev => {
              const d = new Date(prev.year, prev.month - 1);
              return { year: d.getFullYear(), month: d.getMonth() };
            })} className="p-1 rounded hover:bg-slate-800 text-slate-400">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-slate-200 min-w-[100px] text-center">
              {new Date(selectedMonth.year, selectedMonth.month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
            </span>
            <button onClick={() => {
              const now = new Date();
              const d = new Date(selectedMonth.year, selectedMonth.month + 1);
              if (d <= new Date(now.getFullYear(), now.getMonth() + 1)) {
                setSelectedMonth({ year: d.getFullYear(), month: d.getMonth() });
              }
            }} className="p-1 rounded hover:bg-slate-800 text-slate-400">
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Week buttons */}
            <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-700/50 ml-2">
              {weeks.map(w => (
                <button key={w.num} onClick={() => setSelectedWeek(w.num)}
                  className={`px-2 py-1 text-xs rounded-md transition-all ${
                    selectedWeek === w.num
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                  title={`${w.sub} ${new Date(selectedMonth.year, selectedMonth.month).toLocaleDateString('en-IN', { month: 'short' })}`}
                >
                  <div className="font-semibold">{w.label}</div>
                  <div className="text-[9px] opacity-70">{w.sub}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-5">
        {flags && flags.length > 0 && (<div className="space-y-2">{flags.map((flag, i) => {
          const isExpanded = expandedFlag === i;
          const styles = severityStyles[flag.severity] || severityStyles.info;
          return (
            <div key={i}>
              <div
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm cursor-pointer ${styles}`}
                onClick={() => setExpandedFlag(isExpanded ? null : i)}
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{flag.message}</span>
                {flag.data?.dates && (
                  <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                )}
              </div>
              {isExpanded && flag.data?.dates && (
                <div className="ml-10 mt-1 mb-2 flex flex-wrap gap-1.5">
                  {flag.data.dates.map(d => (
                    <span key={d} className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-300">{d}</span>
                  ))}
                  {flag.data?.values && (
                    <div className="w-full mt-1 flex flex-wrap gap-1.5">
                      {flag.data.values.map((v, vi) => (
                        <span key={vi} className="text-xs text-slate-400">{flag.data.dates[vi]}: {formatINR(v)}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}</div>)}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard icon={<DollarSign className="w-5 h-5" />} label={`Total Revenue (${weekLabel})`} value={formatINR(summary?.total_revenue)} sub={`of ${formatINR(totalTarget)} target`} color="emerald" />
          <KPICard icon={<Target className="w-5 h-5" />} label="Avg Daily" value={formatINR(summary?.avg_daily_revenue)} sub={avgDailyTarget ? `of ${formatINR(avgDailyTarget)} target` : undefined} color="blue" />
          <KPICard icon={<BarChart3 className="w-5 h-5" />} label="Avg Achievement" value={summary?.avg_completion_pct != null ? `${Math.round(summary.avg_completion_pct)}%` : '--'} color={(summary?.avg_completion_pct || 0) >= 80 ? 'emerald' : (summary?.avg_completion_pct || 0) >= 50 ? 'yellow' : 'red'} />
          <KPICard icon={<Users className="w-5 h-5" />} label="Total Closures" value={totalClosures.toLocaleString('en-IN')} color="cyan" />
        </div>

        {summary?.trend && (
          <div className="flex items-center gap-2 text-sm flex-wrap">
            {trendIcon(summary.trend)}
            <span className={trendColor(summary.trend)}>Overall trend: <span className="font-semibold capitalize">{summary.trend}</span></span>
            {summary.best_day && <span className="text-slate-500 ml-4">Best: {summary.best_day.date?.slice(5)} ({formatShortINR(summary.best_day.revenue)}, {summary.best_day.pct}%)</span>}
            {summary.worst_day && <span className="text-slate-500 ml-2">Worst: {summary.worst_day.date?.slice(5)} ({formatShortINR(summary.worst_day.revenue)}, {summary.worst_day.pct}%)</span>}
          </div>
        )}

        {chartData.length > 0 && (
          <div className="bg-slate-900 border border-slate-700/50 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-300 mb-3">Revenue Trend</h2>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} />
                <YAxis tickFormatter={(v) => formatShortINR(v)} tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' }} formatter={(value, name) => [formatINR(value), name === 'revenue' ? 'Revenue' : 'Target']} labelFormatter={(label) => `Date: ${label}`} />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#revenueGradient)" />
                <Line type="monotone" dataKey="target" stroke="#64748b" strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
                {chartData.filter((d) => d.isBest).map((d, i) => (<ReferenceDot key={`best-${i}`} x={d.date} y={d.revenue} r={6} fill="#10b981" stroke="#fff" strokeWidth={2} />))}
                {chartData.filter((d) => d.isWorst).map((d, i) => (<ReferenceDot key={`worst-${i}`} x={d.date} y={d.revenue} r={6} fill="#ef4444" stroke="#fff" strokeWidth={2} />))}
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-5 mt-2 text-[10px] text-slate-500 justify-center">
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500 inline-block rounded" /> Revenue</span>
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-slate-500 inline-block rounded" /> Target</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500 rounded-full inline-block" /> Best Day</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-500 rounded-full inline-block" /> Worst Day</span>
            </div>
          </div>
        )}

        {daily && daily.length > 0 && (
          <div className="bg-slate-900 border border-slate-700/50 rounded-xl overflow-hidden">
            <h2 className="text-sm font-semibold text-slate-300 px-4 py-3 border-b border-slate-700/50">Daily Breakdown</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-800/50 text-slate-400">
                    <th className="px-4 py-2 text-left font-medium">Date</th>
                    <th className="px-4 py-2 text-right font-medium">Revenue</th>
                    <th className="px-4 py-2 text-right font-medium">Target</th>
                    <th className="px-4 py-2 text-right font-medium">Achievement</th>
                    <th className="px-4 py-2 text-right font-medium">Closures</th>
                    <th className="px-4 py-2 text-right font-medium">Staff</th>
                    <th className="px-4 py-2 text-center font-medium">Trend</th>
                    <th className="px-4 py-2 w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {daily.map((day, idx) => {
                    const isExpanded = expandedDay === day.date;
                    const pctColor = (day.completion_pct || 0) >= 80 ? 'text-emerald-400' : (day.completion_pct || 0) >= 50 ? 'text-yellow-400' : 'text-red-400';
                    const gymBreakdown = day.gym_breakdown && day.gym_breakdown.length > 0
                      ? day.gym_breakdown
                      : (gyms || []).map((g) => { const dayData = (g.daily || []).find((dd) => dd.date === day.date); return dayData ? { gym_name: g.gym_name, ...dayData } : null; }).filter(Boolean);
                    return (
                      <React.Fragment key={day.date}>
                        <tr className={`border-t border-slate-800/50 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-800/30' : 'hover:bg-slate-800/20'}`} onClick={() => setExpandedDay(isExpanded ? null : day.date)}>
                          <td className="px-4 py-2.5 text-slate-200 font-medium">{day.date?.slice(5)}</td>
                          <td className="px-4 py-2.5 text-right text-emerald-400 font-semibold tabular-nums">{formatINR(day.revenue_done)}</td>
                          <td className="px-4 py-2.5 text-right text-slate-400 tabular-nums">{formatINR(day.target)}</td>
                          <td className={`px-4 py-2.5 text-right font-bold tabular-nums ${pctColor}`}>{day.completion_pct != null ? `${Math.round(day.completion_pct)}%` : '--'}</td>
                          <td className="px-4 py-2.5 text-right text-slate-300 tabular-nums">{displayClosures(day.closures)}</td>
                          <td className="px-4 py-2.5 text-right text-slate-400 tabular-nums">{day.staff_present != null ? `${day.staff_present}/${day.staff_total || '--'}` : '--'}</td>
                          <td className="px-4 py-2.5 text-center">{idx > 0 && daily[idx - 1] ? ((day.revenue_done || 0) > (daily[idx - 1].revenue_done || 0) ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400 mx-auto" /> : (day.revenue_done || 0) < (daily[idx - 1].revenue_done || 0) ? <TrendingDown className="w-3.5 h-3.5 text-red-400 mx-auto" /> : <Minus className="w-3.5 h-3.5 text-slate-500 mx-auto" />) : <Minus className="w-3.5 h-3.5 text-slate-600 mx-auto" />}</td>
                          <td className="px-4 py-2.5 text-center">{isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}</td>
                        </tr>
                        {isExpanded && gymBreakdown.length > 0 && (
                          <tr><td colSpan={8} className="bg-slate-800/20 px-6 py-2"><div className="space-y-1">{gymBreakdown.map((gb) => (<div key={gb.gym_name} className="flex items-center justify-between text-[11px] py-1 border-b border-slate-700/30 last:border-b-0"><span className="text-slate-300 flex-1">{gb.gym_name}</span><span className="text-emerald-400 font-semibold tabular-nums w-24 text-right">{formatINR(gb.revenue)}</span><span className={`font-bold tabular-nums w-16 text-right ${(gb.pct || 0) >= 80 ? 'text-emerald-400' : (gb.pct || 0) >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>{gb.pct != null ? `${Math.round(gb.pct)}%` : '--'}</span><span className="text-slate-500 tabular-nums w-16 text-right">{displayClosures(gb.closures)} cls</span></div>))}</div></td></tr>
                        )}
                        {isExpanded && gymBreakdown.length === 0 && (<tr><td colSpan={8} className="bg-slate-800/20 px-6 py-3 text-[11px] text-slate-500">No per-gym breakdown available for this day.</td></tr>)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {gyms && gyms.length > 0 && (
          <div className="bg-slate-900 border border-slate-700/50 rounded-xl overflow-hidden">
            <h2 className="text-sm font-semibold text-slate-300 px-4 py-3 border-b border-slate-700/50">Per-Gym Performance ({weekLabel})</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-800/50 text-slate-400">
                    <th className="px-4 py-2 text-left font-medium">Gym</th>
                    <th className="px-4 py-2 text-right font-medium">{weekLabel} Revenue</th>
                    <th className="px-4 py-2 text-right font-medium">Avg/Day</th>
                    <th className="px-4 py-2 text-right font-medium">Daily Target</th>
                    <th className="px-4 py-2 text-right font-medium">Avg Achievement</th>
                    <th className="px-4 py-2 text-center font-medium">Above / Below</th>
                    <th className="px-4 py-2 text-center font-medium">Trend</th>
                    <th className="px-4 py-2 w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {gyms.map((gym) => {
                    const isGymExpanded = expandedGym === gym.gym_id;
                    const avgPctColor = (gym.avg_completion_pct || 0) >= 80 ? 'text-emerald-400' : (gym.avg_completion_pct || 0) >= 50 ? 'text-yellow-400' : 'text-red-400';
                    return (
                      <React.Fragment key={gym.gym_id || gym.gym_name}>
                        <tr className={`border-t border-slate-800/50 cursor-pointer transition-colors ${isGymExpanded ? 'bg-slate-800/30' : 'hover:bg-slate-800/20'}`} onClick={() => setExpandedGym(isGymExpanded ? null : gym.gym_id)}>
                          <td className="px-4 py-2.5 text-slate-200 font-medium max-w-[200px] truncate">{gym.gym_name}</td>
                          <td className="px-4 py-2.5 text-right text-emerald-400 font-semibold tabular-nums">{formatINR(gym.total_revenue)}</td>
                          <td className="px-4 py-2.5 text-right text-slate-300 tabular-nums">{formatINR(gym.avg_daily)}</td>
                          <td className="px-4 py-2.5 text-right text-slate-400 tabular-nums">{formatINR(gym.daily_target)}</td>
                          <td className={`px-4 py-2.5 text-right font-bold tabular-nums ${avgPctColor}`}>{gym.avg_completion_pct != null ? `${Math.round(gym.avg_completion_pct)}%` : '--'}</td>
                          <td className="px-4 py-2.5 text-center tabular-nums"><span className="text-emerald-400">{gym.days_above_target ?? '--'}</span><span className="text-slate-600 mx-1">/</span><span className="text-red-400">{gym.days_below_target ?? '--'}</span></td>
                          <td className="px-4 py-2.5 text-center">{trendIcon(gym.trend)}</td>
                          <td className="px-4 py-2.5 text-center">{isGymExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}</td>
                        </tr>
                        {isGymExpanded && gym.daily && gym.daily.length > 0 && (
                          <tr><td colSpan={8} className="bg-slate-800/20 px-4 py-3">
                            <div className="text-[10px] text-slate-500 mb-2">Daily revenue for {gym.gym_name}</div>
                            <ResponsiveContainer width="100%" height={120}>
                              <BarChart data={[...gym.daily].reverse()} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5) || ''} tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} />
                                <YAxis tickFormatter={(v) => formatShortINR(v)} tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} width={55} />
                                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '6px', fontSize: '11px' }} formatter={(value) => [formatINR(value), 'Revenue']} labelFormatter={(label) => `Date: ${label?.slice(5) || label}`} />
                                <Bar dataKey="revenue" radius={[3, 3, 0, 0]}>{[...gym.daily].reverse().map((entry, i) => (<Cell key={i} fill={(entry.pct || 0) >= 80 ? '#10b981' : (entry.pct || 0) >= 50 ? '#eab308' : '#ef4444'} fillOpacity={0.8} />))}</Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </td></tr>
                        )}
                        {isGymExpanded && (!gym.daily || gym.daily.length === 0) && (<tr><td colSpan={8} className="bg-slate-800/20 px-6 py-3 text-[11px] text-slate-500">No daily data available for this gym.</td></tr>)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ icon, label, value, sub, color = 'slate' }) {
  const colors = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
    red: 'bg-red-500/10 border-red-500/30 text-red-400',
    cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
    slate: 'bg-slate-800 border-slate-700 text-slate-300',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${colors[color] || colors.slate}`}>
      <div className="flex items-center gap-2 mb-1 opacity-70">
        {icon}
        <span className="text-[10px] uppercase tracking-wide font-medium">{label}</span>
      </div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}
